import { Classification } from '@/components/classification';
import { SuggestFnProps, SuggestionItem, SuggestResponseType } from '@/components/filters/suggest';
import { SimpleTooltip } from '@/components/simpleTooltip';
import { GraphQLService } from '@/services/graphQLService';
import { FormattedMessage } from 'react-intl';
import { CANCEL_REQUEST, fetchWithCancel } from './fetchWithCancel';
import { stringify } from './querystring';
import { apiConstants } from '@/config/apiConstants';

export type SuggestConfig = {
  render?: (item: SuggestionItem) => React.ReactNode;
  getSuggestions: (props: SuggestFnProps) => SuggestResponseType;
  placeholder?: string;
  getStringValue?: (item: SuggestionItem) => string;
  // clear the search input after an item is selected
  clearOnSelect?: boolean;
};

export const institutionKeySuggest: SuggestConfig = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/grscicoll/institution/suggest?limit=20&q=${q}`
    );
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.map((item: { key: string; name: string }) => ({
          key: item.key,
          title: item.name,
        }));
      });
    return { cancel, promise: result };
  },
};

export const collectionKeySuggest = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/grscicoll/collection/suggest?limit=20&q=${q}`
    );
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.map((item: { key: string; name: string }) => ({
          key: item?.key,
          title: item?.name,
        }));
      });
    return { cancel, promise: result };
  },
};

export const datasetKeySuggest: SuggestConfig = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/dataset/suggest?limit=20&q=${q}`
    );
    const result = promise.then((res) => res.json());
    return { cancel, promise: result };
  },
  render: (item: SuggestionItem) => {
    return <div className="g-line-clamp-3">{item.title}</div>;
  },
};

export const datasetKeyOccurrenceSuggest = {
  getSuggestions: ({ q, siteConfig, searchContext }: SuggestFnProps): SuggestResponseType => {
    const abortController = new AbortController();
    const graphqlService = new GraphQLService({
      endpoint: siteConfig.graphqlEndpoint,
      abortSignal: abortController.signal,
      locale: 'en',
    });

    const rootPredicate = searchContext?.scope;
    const SEARCH = `
    query keywordSearch($q: String, $predicate: Predicate, $size: Int){
      occurrenceDatasetSuggest(q: $q, predicate: $predicate, size: $size) {
        key
        count
        dataset {
          title
        }
      }
    }
    `;

    const variables = {
      size: 10,
      predicate: rootPredicate,
      q,
    };
    const promise = graphqlService.query<
      {
        occurrenceDatasetSuggest?: { key: string; count: number; dataset: { title: string } }[];
      },
      typeof variables
    >(SEARCH, variables);
    return {
      promise: promise
        .then((res) => res.json())
        .then((response) => {
          // undefined when the query fails; the Suggest component tolerates that
          return response.data?.occurrenceDatasetSuggest?.map((i) => ({
            ...i,
            title: i.dataset.title,
          })) as SuggestionItem[];
        }),
      cancel: () => abortController.abort(CANCEL_REQUEST),
    };
  },
};

export const publisherKeySuggest = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/organization/suggest?limit=20&q=${q}`
    );
    const result = promise.then((res) => res.json());
    return { cancel, promise: result };
  },
};

export const publisherKeyOccurrenceSuggest = {
  getSuggestions: ({ q, siteConfig, searchContext }: SuggestFnProps): SuggestResponseType => {
    const abortController = new AbortController();
    const graphqlService = new GraphQLService({
      endpoint: siteConfig.graphqlEndpoint,
      abortSignal: abortController.signal,
      locale: 'en',
    });

    const rootPredicate = searchContext?.scope;
    const SEARCH = `
    query keywordSearch($q: String, $predicate: Predicate, $size: Int){
      occurrencePublisherSuggest(q: $q, predicate: $predicate, size: $size) {
        key
        count
        item: publisher {
          title
        }
      }
    }
    `;

    const variables = {
      size: 10,
      predicate: rootPredicate,
      q,
    };
    const promise = graphqlService.query<
      {
        occurrencePublisherSuggest?: { key: string; count: number; item: { title: string } }[];
      },
      typeof variables
    >(SEARCH, variables);
    return {
      promise: promise
        .then((res) => res.json())
        .then((response) => {
          // undefined when the query fails; the Suggest component tolerates that
          return response.data?.occurrencePublisherSuggest?.map((i) => ({
            ...i,
            title: i.item.title,
          })) as SuggestionItem[];
        }),
      cancel: () => abortController.abort(CANCEL_REQUEST),
    };
  },
};

export const networkKeySuggest = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(`${siteConfig.v1Endpoint}/network?limit=20&q=${q}`);
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.results.map((item: { key: string; title: string }) => ({
          key: item?.key,
          title: item?.title,
        }));
      });
    return { cancel, promise: result };
  },
};

const HIGHER_RANKS = ['kingdom', 'phylum', 'class', 'order', 'family', 'genus', 'species'] as const;

// item shape from the v1 species suggest API
type TaxonSuggestionItem = SuggestionItem & {
  status?: string;
  rank?: string;
} & Partial<Record<(typeof HIGHER_RANKS)[number], string>>;

export function TaxonDetailsLabel(taxon: TaxonSuggestionItem) {
  const ranks = HIGHER_RANKS.map((rank) => {
    return taxon[rank] && rank !== taxon.rank?.toLowerCase() ? (
      <span key={rank}>{taxon[rank]}</span>
    ) : null;
  });

  return (
    <div style={{ maxWidth: '100%' }}>
      <div style={{ color: '#aaa', fontSize: '0.85em' }}>
        <Classification>{ranks}</Classification>
      </div>
    </div>
  );
}

export const taxonKeySuggest = {
  render: (item: TaxonSuggestionItem) => {
    return (
      <div>
        {item.status !== 'ACCEPTED' && (
          <SimpleTooltip
            title={
              <span>
                <FormattedMessage id={`enums.taxonomicStatus.${item.status}`} />
              </span>
            }
          >
            <span
              style={{
                display: 'inline-block',
                marginInlineEnd: 8,
                width: 8,
                height: 8,
                borderRadius: 4,
                background: 'orange',
              }}
            ></span>
          </SimpleTooltip>
        )}
        {item.title}
        <div>
          <TaxonDetailsLabel {...item} />
        </div>
      </div>
    );
  },
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/species/suggest?limit=20&q=${q}`
    );
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.map((item: { key: string; scientificName: string }) => ({
          title: item?.scientificName,
          ...item,
        }));
      });
    return { cancel, promise: result };
  },
};

export type TaxonSuggestType = SuggestFnProps & { checklistKey?: string | number };

// https://api.gbif.org/v2/taxon/suggest/90d9e8a6-0ce1-472d-b682-3451095dbc5a?q=Ernobius%20tabidus
// item shape from the v2 taxon suggest API
type ClbTaxonSuggestionItem = SuggestionItem & {
  isSynonym?: boolean;
  taxonomicStatus?: string;
  scientificName?: string;
  acceptedNameUsage?: string;
  context?: string;
  rankLabel?: string;
};

export const taxonKeyClbSuggest = {
  render: (item: ClbTaxonSuggestionItem) => {
    const isSynonym = item.isSynonym;
    const notAccepted = item?.taxonomicStatus !== 'ACCEPTED';
    return (
      <div>
        {item.scientificName}
        <div style={{ maxWidth: '100%', fontSize: '0.85em' }}>
          {isSynonym && (
            <div className="g-text-orange-500">Synonym for {item.acceptedNameUsage}</div>
          )}
          {notAccepted && !isSynonym && (
            <div className="g-text-orange-500">
              <FormattedMessage
                id={`enums.clbTaxonomicStatus.${(item?.taxonomicStatus ?? '').replace(' ', '_')}`}
                defaultMessage={item?.taxonomicStatus}
              />
            </div>
          )}
          {!isSynonym && item.context && (
            <div style={{ color: '#aaa' }}>
              <FormattedMessage
                id="search.rankInGroup"
                values={{ rank: item.rankLabel, group: item.context }}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
  getSuggestions: ({ q, checklistKey, intl }: TaxonSuggestType): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${apiConstants.taxonApi}/suggest/${checklistKey}?limit=20&q=${q}`
    );
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.map(
          (item: {
            taxonID: string;
            scientificName: string;
            taxonRank: string;
            taxonomicStatus: string;
            acceptedNameUsage?: string;
            acceptedNameUsageID?: string;
          }) => ({
            title: item?.scientificName,
            key: item?.taxonID,
            isSynonym: !!item.acceptedNameUsageID,
            rankLabel: intl.formatMessage({
              id: `enums.taxonRank.${item.taxonRank}`,
              defaultMessage: item.taxonRank,
            }),
            ...item,
          })
        );
      });
    return { cancel, promise: result };
  },
};

export const gadGidSuggest = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const gadmSuggest = siteConfig?.suggest?.gadm;
    const extraParams = gadmSuggest?.type === 'PARAMS' ? stringify(gadmSuggest?.value ?? {}) : '';
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/geocode/gadm/search?limit=100&q=${q}&${extraParams}`
    );
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.results.map(
          (item: { id: string; name: string; higherRegions?: { name: string }[] }) => ({
            ...item,
            key: item?.id,
            title: item?.name,
            description: item?.higherRegions && item?.higherRegions?.length > 0 && (
              <Classification>
                {item.higherRegions.map((x) => (
                  <span>{x.name}</span>
                ))}
              </Classification>
            ),
          })
        );
      });
    return { cancel, promise: result };
  },
  render: function GadmGidSuggestItem(
    suggestion: SuggestionItem & { higherRegions?: { name: string }[] }
  ) {
    return (
      <div className="g-max-w-full">
        <div>{suggestion.title}</div>
        {suggestion.higherRegions && suggestion.higherRegions.length > 0 && (
          <Classification className="g-text-slate-500">
            {suggestion.higherRegions.map((x) => (
              <span>{x.name}</span>
            ))}
          </Classification>
        )}
      </div>
    );
  },
};

export const institutionCodeSuggest = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/occurrence/search/institutionCode?limit=100&q=${q}`
    );
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.map((item: string) => ({
          key: item,
          title: item,
        }));
      });
    return { cancel, promise: result };
  },
};

export const collectionCodeSuggest = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/occurrence/search/collectionCode?limit=100&q=${q}`
    );
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.map((item: string) => ({
          key: item,
          title: item,
        }));
      });
    return { cancel, promise: result };
  },
};

export const recordNumberSuggest = {
  getSuggestions: ({ q, siteConfig }: SuggestFnProps): SuggestResponseType => {
    const { cancel, promise } = fetchWithCancel(
      `${siteConfig.v1Endpoint}/occurrence/search/recordNumber?limit=100&q=${q}`
    );
    const result = promise
      .then((res) => res.json())
      .then((data) => {
        return data.map((item: string) => ({
          key: item,
          title: item,
        }));
      });
    return { cancel, promise: result };
  },
};

// for use with vocabulary endpoints
export type VocabularyType = {
  name: string;
  label: { key: number; value: string; language: string }[];
};
function extractTitle(vocabularyLocale: string) {
  // response is the raw REST payload; the result is wrapped in `data`
  return (response: { results: VocabularyType[]; data?: Record<string, unknown> }) => {
    // transform result labels to an object with language as keys
    const results = response?.results?.map((result) => {
      const labels = result.label.reduce((acc: Record<string, string>, label) => {
        acc[label.language] = label.value;
        return acc;
      }, {});
      return {
        ...result,
        title: labels[vocabularyLocale] || labels.en || result.name || 'Unknown',
      };
    });
    return { data: { ...response.data, results } };
  };
}

export const establishmentMeansSuggest = vocabularySuggest('EstablishmentMeans');
export const institutionDisciplineSuggest = vocabularySuggest('Discipline');
export const institutionTypeSuggest = vocabularySuggest('InstitutionType');
export const preservationTypeSuggest = vocabularySuggest('PreservationType');
export const biomeTypeSuggest = vocabularySuggest('BiomeType');
export const objectClassificationSuggest = vocabularySuggest('ObjectClassificationName');
export const pathwaySuggest = vocabularySuggest('Pathway');
export const degreeOfEstablishmentSuggest = vocabularySuggest('DegreeOfEstablishment');
export const collectionContentTypeSuggest = vocabularySuggest('CollectionContentType');
export const datasetCategorySuggest = vocabularySuggest('DatasetCategory');
export const typeStatusSuggest = vocabularySuggest('TypeStatus');
export const geoTimeSuggest = vocabularySuggest('GeoTime');

function vocabularySuggest(vocabularyName: string) {
  return {
    getSuggestions: ({
      q,
      limit = 100,
      siteConfig,
      currentLocale,
    }: SuggestFnProps): SuggestResponseType => {
      const vocabularyLocale = currentLocale.vocabularyLocale ?? currentLocale.localeCode ?? 'en';
      const { cancel, promise } = fetchWithCancel(
        `${siteConfig.v1Endpoint}/vocabularies/${vocabularyName}/concepts?limit=${limit}&q=${q}&lang=${vocabularyLocale}`
      );
      const result = promise
        .then((res) => res.json())
        .then(extractTitle(vocabularyLocale))
        .then((response) => {
          return response.data.results.map((item) => ({
            key: item.name,
            title: item.title,
          }));
        });
      return { cancel, promise: result };
    },
  };
}
